import * as React from "react";
import { useContext } from "react";
import { runInAction } from "mobx";
import { RootStoreContext } from "../../../stores/RootStore";
import { cancelCheckin } from "../../../api/API";
import './styles.css';

interface CancelCheckinPopupProps {
  item: {
    time: {
      date: string;
      hour: number;
      minute: number;
    };
    trainingPeriodId: number;
  };
  onClose: () => void;
  onSuccess: () => void;
}

const CancelCheckinPopup: React.FC<CancelCheckinPopupProps> = ({ item, onClose, onSuccess }) => {
  const { studioConfigStore, registrationStore } = useContext(RootStoreContext);
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [cancelError, setCancelError] = React.useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = React.useState<{ penaltyCoins: number } | null>(null);

  const handleOverlayClick = () => {
    if (!isCancelling && !cancelSuccess) {
      onClose();
    }
  };

  const handleKeepCheckin = () => {
    setCancelError(null);
    onClose();
  };

  const handleSuccessClose = () => {
    setCancelSuccess(null);
    onClose();
    onSuccess();
  };

  const handleCancelCheckin = async () => {
    setIsCancelling(true);
    setCancelError(null);
    try {
      // Build the class time from item.time
      const classDateTime = new Date(item.time.date);
      classDateTime.setHours(item.time.hour, item.time.minute, 0, 0);

      const response = await cancelCheckin({
        trainingPeriodId: item.trainingPeriodId,
        classTime: classDateTime.toISOString(),
        userId: registrationStore.user.id
      });

      // Update available coins: return original class cost and deduct penalty
      runInAction(() => {
        registrationStore.user.latestTrainingPackage.availableCoins += (response.classCoins - response.penaltyCoins);
      });

      setCancelSuccess({ penaltyCoins: response.penaltyCoins });
    } catch (error) {
      setCancelError('Error cancelling the class check-in. Please contact our admin.');
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="cancel-popup-overlay" onClick={handleOverlayClick}>
      <div className="cancel-popup" onClick={e => e.stopPropagation()}>
        {!isCancelling && !cancelSuccess && (
          <button className="cancel-popup-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
        <div className="cancel-popup-content">
          <h3>{cancelSuccess ? 'Cancellation Successful' : 'Cancel Check-in'}</h3>
          {cancelSuccess ? (
            <p className="cancel-success">
              The class check-in has been cancelled.
            </p>
          ) : cancelError ? (
            <p className="cancel-error">
              {cancelError}
            </p>
          ) : (
            <p>
              Are you sure you wish to cancel the class check-in? The cancellation fee is {studioConfigStore.classCancelCoins} coins.
            </p>
          )}
        </div>
        <div className="cancel-popup-buttons">
          {cancelSuccess ? (
            <button
              className="cancel-popup-keep"
              onClick={handleSuccessClose}
            >
              Close
            </button>
          ) : (
            <>
              <button
                className="cancel-popup-keep"
                onClick={handleKeepCheckin}
                disabled={isCancelling}
              >
                {cancelError ? 'Close' : 'Keep check-in'}
              </button>
              {!cancelError && (
                <button
                  className="cancel-popup-confirm"
                  disabled={isCancelling}
                  onClick={handleCancelCheckin}
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel check-in'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancelCheckinPopup;
